export const RightPanelMode = {
  STANDARD: 'standard',
  NARROW: 'narrow',
  HIDDEN: 'hidden',
} as const;

export type RightPanelModeType =
  (typeof RightPanelMode)[keyof typeof RightPanelMode];

// --- Continuous right-panel width (VE-25218 part 2) ---------------------
// The panel is drag-resizable to any width; the presets survive as targets
// for the toggle button / double-click, and RightPanelMode is DERIVED from
// the width + hidden flag so preset-based consumers keep working.

export const RIGHT_PANEL_STANDARD_WIDTH = 580;
export const RIGHT_PANEL_NARROW_WIDTH = 450; // min visible width — below it the panel snaps closed
// width <= this reads as NARROW (cramped/short-label styling). Set to the
// midpoint between narrow and standard so it is ALSO the drag snap boundary:
// while dragging, the short labels appearing marks exactly the point where a
// release will snap to narrow (a live "which size" indicator). Keep the two
// uses in sync (usePanelDrag's release snap references this constant).
export const RIGHT_PANEL_NARROW_BREAKPOINT =
  (RIGHT_PANEL_NARROW_WIDTH + RIGHT_PANEL_STANDARD_WIDTH) / 2; // 515
export const RIGHT_PANEL_MAX_WIDTH = 900;
export const RIGHT_PANEL_SNAP_CLOSE_DELTA = 180; // drag hysteresis past min before snapping closed
// Fraction of the viewport the panel may occupy at most
export const RIGHT_PANEL_MAX_VIEWPORT_FRACTION = 0.55;

// When true, only the audio/transcript tab gets the continuous drag-resize
// (where the extra width is actually useful); every other tab (video/image/
// comments) uses the 3 presets via the toggle button — no drag bar — and the
// snap-close guide line is hidden. Flip to false for continuous drag on all
// tabs. (The draggable range on non-audio tabs is only ~110px, so the drag
// adds little there.)
export const VIDEO_TAB_PRESETS_ONLY = true;

export const getRightPanelMaxWidth = (
  viewportWidth: number,
  imageMode = false,
  // only the audio/transcript tab may drag the panel past the standard width
  allowWide = false
) => {
  if (imageMode) {
    return RIGHT_PANEL_NARROW_WIDTH;
  }
  const viewportMax = Math.max(
    RIGHT_PANEL_NARROW_WIDTH,
    Math.min(
      RIGHT_PANEL_MAX_WIDTH,
      Math.round(viewportWidth * RIGHT_PANEL_MAX_VIEWPORT_FRACTION)
    )
  );
  return allowWide
    ? viewportMax
    : Math.min(viewportMax, RIGHT_PANEL_STANDARD_WIDTH);
};

export const deriveRightPanelMode = (
  width: number,
  isHidden: boolean
): RightPanelModeType => {
  if (isHidden) {
    return RightPanelMode.HIDDEN;
  }
  return width <= RIGHT_PANEL_NARROW_BREAKPOINT
    ? RightPanelMode.NARROW
    : RightPanelMode.STANDARD;
};

export interface LayoutModeProps {
  readonly rightPanelMode?: RightPanelModeType;
  readonly isShowPreviewOptions?: boolean;
  readonly isExpandVideoMode?: boolean;
  readonly customTimelineHeight?: number;
  readonly isColumnMainButtons?: boolean;
  onTimelineHeightChange?: (height: number) => void;
  setTimelineScrollButtonContainerWidth?: (width: number) => void;
  timelineScrollButtonContainerWidth?: number;
  fullTimelineHeight?: number;
  minHeight?: number;
  maxHeight?: number;
  imageMode?: boolean;
}

export const ACTION_TYPE = {
  MOVE: 'move',
  RESIZE: 'resize',
  LIVE_TRACKING: 'live_tracking',
} as const;

export type ActionType = (typeof ACTION_TYPE)[keyof typeof ACTION_TYPE];

export const REDACTION_CODE_CHANGE = {
  ADD: 'ADD',
  REMOVE: 'REMOVE',
  UPDATE: 'UPDATE',
} as const;

export type RedactionCodeChangeType =
  (typeof REDACTION_CODE_CHANGE)[keyof typeof REDACTION_CODE_CHANGE];
